import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Icon,
  Link,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';
import Loader from './Loader';
import ControlPanel from './ControlPanel';
import { Download01 } from '@kula-ai/icons/dual-tone/Download01';
import { useShowToast } from '@/hooks';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { IconButton } from '@/components/ats';
import { FullScreenModal } from '@/componentsV1/FullScreenModal';
import { Maximize01 } from '@kula-ai/icons/outline/Maximize01';
import { KText } from '@/ui-library/components/text';
import { Replace } from '@/assets/icons/ats';
import { useKFlags } from '@/hooks/use-k-flags';
import { PdfViewer } from '../react-pdf-dev';
import { DocxViewer } from '../DocxViewer';
import {
  ZoomIn,
  ZoomOut,
  Download01 as Download01Core,
  Expand03,
  Minimize01,
} from '@kula-ai/icons-core';

// Only .docx (ZIP+XML) renders client-side via docx-preview. Legacy binary
// .doc (application/msword / OLE2) can't be parsed in the browser — it shows
// only when the server provides a converted preview_url.
const DOCX_CLIENT_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_MIMES = [
  'application/msword',
  DOCX_CLIENT_MIME,
  'application/x-ole-storage',
];

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface Props {
  file: any;
  hideControl?: boolean;
  containerStyles?: FlexProps;
}

export const PDFReader: React.FC<Props> = ({
  file,
  hideControl = false,
  containerStyles = {},
}) => {
  const [scale, setScale] = useState(1.0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (container) {
      const pageElement = container.querySelector(
        `.react-pdf__Page[data-page-number="${pageNumber}"]`
      );
      if (pageElement) {
        container.scrollTo({
          top: (pageElement as HTMLElement).offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }, [pageNumber]);

  return (
    <Box w="100%" h="100%" overflow="hidden">
      <Loader isLoading={isLoading} />
      <Flex
        h="100%"
        overflow="hidden"
        direction="column"
        id="pdf-container"
        border="1px solid"
        borderTopRadius="sm"
        borderColor="border-color"
        {...containerStyles}
      >
        {!hideControl && (
          <ControlPanel
            file={file}
            pageNumber={pageNumber}
            numPages={numPages}
            setPageNumber={setPageNumber}
            scale={scale}
            setScale={setScale}
          />
        )}

        <Box h="100%" overflowY="auto" ref={pdfContainerRef}>
          <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
                renderMode="canvas" // Ensure rendering as canvas for higher quality
              />
            ))}
          </Document>
        </Box>
      </Flex>
    </Box>
  );
};

const IMG_ZOOM_MIN = 0.25;
const IMG_ZOOM_MAX = 4;
const IMG_ZOOM_STEP = 0.25;
const imgClamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

interface ImageViewerProps {
  src: string;
  alt: string;
  onError: () => void;
  onDownload: () => void;
  containerStyles?: BoxProps;
}

const ImgToolButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, disabled, children }) => (
  <Tooltip label={label} placement="bottom" hasArrow openDelay={150}>
    <Flex
      as="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      align="center"
      justify="center"
      w="32px"
      h="32px"
      borderRadius="6px"
      transition="background-color 0.15s"
      _hover={disabled ? {} : { bg: 'secondary-50' }}
      _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
      cursor={disabled ? 'not-allowed' : 'pointer'}
    >
      {children}
    </Flex>
  </Tooltip>
);

const ImageViewer: React.FC<ImageViewerProps> = ({
  src,
  alt,
  onError,
  onDownload,
  containerStyles = {},
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const handleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      viewerRef.current?.requestFullscreen();
    }
  };

  return (
    <Flex
      ref={viewerRef}
      flexDir="column"
      w="100%"
      h="100%"
      bg="white"
      {...containerStyles}
    >
      <Flex
        align="center"
        justify="space-between"
        px="12px"
        py="8px"
        bg="white"
        borderBottom="1px solid"
        borderColor="secondary-200"
        flexShrink={0}
      >
        <Box />
        <Flex align="center" gap="2px">
          <ImgToolButton
            label="Zoom out"
            onClick={() =>
              setScale((s) =>
                imgClamp(s - IMG_ZOOM_STEP, IMG_ZOOM_MIN, IMG_ZOOM_MAX)
              )
            }
            disabled={scale <= IMG_ZOOM_MIN}
          >
            <Icon as={ZoomOut} size={18} color="secondary-700" />
          </ImgToolButton>
          <ImgToolButton
            label="Zoom in"
            onClick={() =>
              setScale((s) =>
                imgClamp(s + IMG_ZOOM_STEP, IMG_ZOOM_MIN, IMG_ZOOM_MAX)
              )
            }
            disabled={scale >= IMG_ZOOM_MAX}
          >
            <Icon as={ZoomIn} size={18} color="secondary-700" />
          </ImgToolButton>
        </Flex>
        <Flex align="center" gap="4px">
          <ImgToolButton label="Download" onClick={onDownload}>
            <Icon as={Download01Core} size={20} color="secondary-700" />
          </ImgToolButton>
          <ImgToolButton
            label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={handleFullscreen}
          >
            <Icon
              as={isFullscreen ? Minimize01 : Expand03}
              size={20}
              color="secondary-700"
            />
          </ImgToolButton>
        </Flex>
      </Flex>
      <Box flex={1} overflow="auto">
        <Flex w="100%" minH="100%" align="center" justify="center" p="16px">
          <img
            src={src}
            alt={alt}
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
              transition: 'transform 0.15s ease',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
            onError={onError}
          />
        </Flex>
      </Box>
    </Flex>
  );
};

interface PDFReaderBrowserProps {
  file: string;
  content_type: string;
  preview_url: string;
  containerStyles?: BoxProps;
  showControls?: boolean;
  showReplaceControl?: boolean;
  hideControl?: boolean;
  isFullscreen?: boolean;
  handleUpload?: (_selectedFiles: File[]) => void;
  acceptedFiles?: string[];
  maxFiles?: number;
  styles?: Record<string, any>;
  fileName?: string;
  updateFromResumeControl?: React.ReactNode;
}

export const PDFReaderBrowser: React.FC<PDFReaderBrowserProps> = ({
  file,
  content_type,
  containerStyles = {},
  showControls = true,
  showReplaceControl = false,
  preview_url = null,
  isFullscreen = false,
  handleUpload = () => {},
  acceptedFiles = [],
  maxFiles = 1,
  styles = {},
  fileName = 'document',
  updateFromResumeControl,
}) => {
  const toast = useShowToast();
  const [pdfError, setPdfError] = useState(false);
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const isPdfFile = content_type === 'application/pdf';
  const isImageFile = !!content_type?.startsWith('image/');
  const isDocxFile = DOCX_MIMES.includes(content_type);
  const attachFileRef = useRef(null);
  const isPreviewAvailable = isDocxFile && !!preview_url;
  // Only .docx falls back to the client-side viewer; binary .doc degrades to download.
  const isDocxClientFallback =
    content_type === DOCX_CLIENT_MIME && !preview_url;
  const fileUrl = isPreviewAvailable ? preview_url : file;

  const { feNewPdfViewer } = useKFlags();

  const fullscreenDisclosure = useDisclosure();

  useEffect(() => {
    const checkFileFormat = () => {
      if (
        !isPdfFile &&
        !isPreviewAvailable &&
        !isImageFile &&
        !isDocxClientFallback
      ) {
        setUnsupportedFormat(true);
        return;
      }
    };

    const checkPdfAvailability = async () => {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('PDF not available');
        }
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfError(true);
      }
    };

    checkFileFormat();
    if (!unsupportedFormat && !isImageFile && !isDocxClientFallback) {
      checkPdfAvailability();
    }
  }, [
    unsupportedFormat,
    fileUrl,
    isPdfFile,
    isPreviewAvailable,
    isImageFile,
    isDocxClientFallback,
  ]);

  const handleDownload = () => {
    downloadFileFromUrl({
      url: fileUrl,
      filename: `${fileName}.pdf`,
      onSuccess: () => {
        toast({ title: `File downloaded` });
      },
      onError: () => {
        toast({ title: 'Failed to download', status: 'error' });
      },
    });
  };

  if (pdfError) {
    return (
      <Box w="100%" h="100%" p={'24px'} {...containerStyles}>
        <KText>Unable to load PDF. Try downloading it instead.</KText>
        <IconButton
          text="Download"
          icon={Download01}
          iconContainerProps={{
            width: 'unset',
            height: 'unset',
            bg: 'unset',
          }}
          iconProps={{ width: '14px', height: '14px' }}
          gap="4px"
          onClick={handleDownload}
        />
      </Box>
    );
  }

  if (unsupportedFormat) {
    return (
      <Box {...containerStyles}>
        <KText fontSize="sm">Resume preview unavailable for this format</KText>
        <IconButton
          mt="14px"
          text="Download"
          icon={Download01}
          iconContainerProps={{
            width: 'unset',
            height: 'unset',
            bg: 'unset',
          }}
          iconProps={{ width: '14px', height: '14px' }}
          gap="4px"
          onClick={handleDownload}
        />
      </Box>
    );
  }

  if (isDocxClientFallback) {
    return (
      <Box
        w="100%"
        h="100%"
        overflow="auto"
        position="relative"
        bg="white"
        {...containerStyles}
      >
        <DocxViewer url={fileUrl} />
      </Box>
    );
  }

  if (isImageFile) {
    return (
      <ImageViewer
        src={fileUrl}
        alt={fileName}
        onError={() => setPdfError(true)}
        onDownload={handleDownload}
        containerStyles={containerStyles}
      />
    );
  }

  if (feNewPdfViewer) {
    return (
      <PdfViewer
        url={fileUrl}
        styles={styles}
        hideToolbar={!showControls}
        handleUpload={handleUpload}
        showReplaceControl={showReplaceControl}
        acceptedFiles={acceptedFiles}
        filename={fileName}
        updateFromResumeControl={
          showControls ? updateFromResumeControl : undefined
        }
      />
    );
  }

  const handleIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = event.currentTarget;

    // Confirm the iframe content has been loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      setIsPdfLoaded(true);
    } else {
      setTimeout(() => setIsPdfLoaded(true), 100); // Fallback for delayed state
    }
  };

  const iframeSource = showControls
    ? fileUrl
    : `${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`;

  const renderFullscreen = () => {
    return (
      <Flex
        position="absolute"
        top="13px"
        right="124px"
        onClick={fullscreenDisclosure.onOpen}
      >
        <Flex
          justify="center"
          align="center"
          w="28px"
          h="28px"
          borderRadius="full"
          transition="background-color 0.2s"
          cursor="pointer"
          _hover={{
            bg: 'rgb(66 70 73)',
          }}
          title="Fullscreen"
        >
          <Icon as={Maximize01} w="15px" h="15px" color="white" />
        </Flex>
      </Flex>
    );
  };

  const renderReplace = () => {
    return (
      <Flex
        cursor="pointer"
        position="absolute"
        top="13px"
        right="154px"
        onClick={() => attachFileRef?.current?.click()}
      >
        <Flex
          cursor="pointer"
          justify="center"
          align="center"
          gap="6px"
          p="4px 8px"
          borderRadius="xs"
          transition="background-color 0.2s"
          _hover={{
            bg: 'rgb(66 70 73)',
          }}
          title="Replace resume"
        >
          <input
            ref={attachFileRef}
            type="file"
            accept={acceptedFiles.join(', ')}
            onChange={(event) => {
              if (event?.target?.files?.length) {
                handleUpload(Array.from(event?.target?.files));
              }
            }}
            max={maxFiles}
            style={{ visibility: 'hidden', height: 0, width: 0 }}
          />
          <Icon as={Replace} w="15px" h="15px" color="white" />
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Box
        w="100%"
        h="100%"
        overflow="hidden"
        position="relative"
        {...containerStyles}
      >
        {!isFullscreen && showControls && isPdfLoaded && (
          <>
            {showReplaceControl && renderReplace()}
            {renderFullscreen()}
            {updateFromResumeControl && (
              <Box position="absolute" top="8px" right="170px" zIndex={1}>
                {updateFromResumeControl}
              </Box>
            )}
          </>
        )}
        <iframe
          src={iframeSource}
          width="100%"
          height="100%"
          style={{ border: 'none', background: 'white' }}
          title="PDF Viewer"
          onLoad={handleIframeLoad}
        >
          <p>
            Your browser does not support PDF viewing.
            <Link href={fileUrl} download>
              Click here to download the PDF file.
            </Link>
          </p>
        </iframe>
      </Box>
      {fullscreenDisclosure.isOpen && (
        <FullScreenModal
          isOpen={fullscreenDisclosure.isOpen}
          onClose={fullscreenDisclosure.onClose}
        >
          <Box h="100%" w="100%">
            <PDFReaderBrowser
              file={file}
              content_type={content_type}
              containerStyles={containerStyles}
              showControls={showControls}
              preview_url={preview_url}
              fileName={fileName}
              isFullscreen
            />
          </Box>
        </FullScreenModal>
      )}
    </>
  );
};
